/**
 * Lap Processing Stage
 *
 * Creates Lap objects from lap bounds and synchronized telemetry
 */

import {
  PipelineStage,
  PipelineContext,
  StageResult,
} from '../ProcessingPipeline'
import { Lap } from '../../../domain/lap/Lap'
import { LapMetadata } from '../../../domain/lap/LapMetadata'
import { ProcessedTelemetryData } from '../../../domain/telemetry/processed/ProcessedTelemetryData'
import { RawTelemetryData } from '../../../domain/telemetry/raw/RawTelemetryData'
import { GpsPoint } from '../../../domain/telemetry/processed/GpsPoint'
import { LeanAnglePoint } from '../../../domain/telemetry/processed/LeanAngle'
import { AccelerationPoint } from '../../../domain/telemetry/processed/Acceleration'
import { GForcePoint } from '../../../domain/telemetry/processed/GForce'
import { TelemetryStatistics } from '../../../domain/telemetry/processed/TelemetryStatistics'
import { calculateSamplingFrequencyHz } from '../../../utils/timeUtils'

/**
 * Generate a simple unique ID
 */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

export class LapProcessingStage implements PipelineStage {
  name = 'Lap Processing'

  async execute(context: PipelineContext): Promise<StageResult<void>> {
    const warnings: string[] = []

    try {
      const { lapBounds, synchronizedTelemetry, rawTelemetry, dto, track } =
        context

      if (!lapBounds || lapBounds.length === 0) {
        return {
          success: false,
          error: 'No lap bounds available',
        }
      }

      if (!synchronizedTelemetry) {
        return {
          success: false,
          error: 'No synchronized telemetry available',
        }
      }

      if (!rawTelemetry) {
        return {
          success: false,
          error: 'No raw telemetry available',
        }
      }

      const laps: Lap[] = []

      // Process each lap
      for (let i = 0; i < lapBounds.length; i++) {
        const bounds = lapBounds[i]
        const lapNumber = i + 1

        // Extract telemetry for this lap
        const lapTelemetry = this.extractLapTelemetry(
          synchronizedTelemetry,
          bounds.startTime,
          bounds.finishTime
        )

        if (lapTelemetry.gpsPoints.length === 0) {
          warnings.push(`Lap ${lapNumber} has no telemetry data`)
          continue
        }

        // Extract lap-specific raw telemetry
        const lapRawTelemetry = this.extractLapRawTelemetry(
          rawTelemetry,
          bounds.startTime,
          bounds.finishTime
        )

        // Check if we have lap DTO for this lap
        const lapDto = context.lapDtos?.find(
          (dto) =>
            Math.abs(
              new Date(dto.startTime).getTime() - bounds.startTime.getTime()
            ) < 1000
        )

        // Create lap metadata
        const metadata: LapMetadata = {
          lapId: lapDto?.id ?? generateId(),
          lapNumber,
          driverName: 'Unknown', // DTO doesn't contain driver info
          trackSessionId: dto.id,
          trackId: track.id,
        }

        // Create lap with raw telemetry
        const lap = new Lap(metadata, bounds, lapTelemetry, lapRawTelemetry)
        laps.push(lap)
      }

      context.laps = laps

      if (laps.length === 0) {
        return {
          success: false,
          error: 'No laps could be created',
        }
      }

      return {
        success: true,
        warnings: warnings.length > 0 ? warnings : undefined,
      }
    } catch (error) {
      return {
        success: false,
        error: `Lap processing failed: ${error instanceof Error ? error.message : String(error)}`,
      }
    }
  }

  /**
   * Extract raw telemetry for a specific lap time range
   */
  private extractLapRawTelemetry(
    sessionRawTelemetry: RawTelemetryData,
    startTime: Date,
    finishTime: Date
  ): RawTelemetryData {
    const startMs = startTime.getTime()
    const finishMs = finishTime.getTime()

    // Filter GPS readings for this lap's time range
    const lapGpsReadings = sessionRawTelemetry.gps.filter(
      (reading) =>
        reading.timestamp.getTime() >= startMs &&
        reading.timestamp.getTime() <= finishMs
    )

    // Adjust distance to be lap-relative (starting from 0)
    const startDistance = lapGpsReadings[0]?.distanceMeters ?? 0
    const adjustedGpsReadings = lapGpsReadings.map((reading) => ({
      ...reading,
      distanceMeters: reading.distanceMeters
        ? reading.distanceMeters - startDistance
        : 0,
    }))

    return {
      gps: adjustedGpsReadings,
      gyroscope: sessionRawTelemetry.gyroscope?.filter(
        (reading) =>
          reading.timestamp.getTime() >= startMs &&
          reading.timestamp.getTime() <= finishMs
      ),
      accelerometer: sessionRawTelemetry.accelerometer?.filter(
        (reading) =>
          reading.timestamp.getTime() >= startMs &&
          reading.timestamp.getTime() <= finishMs
      ),
      recordingStartTime: startTime,
      device: sessionRawTelemetry.device,
    }
  }

  /**
   * Extract telemetry for a specific time range
   */
  private extractLapTelemetry(
    fullTelemetry: ProcessedTelemetryData,
    startTime: Date,
    finishTime: Date
  ): ProcessedTelemetryData {
    const startMs = startTime.getTime()
    const finishMs = finishTime.getTime()

    // Extract GPS points
    const gpsPoints = this.extractPointsInTimeRange(
      fullTelemetry.gpsPoints,
      startMs,
      finishMs
    )

    // Recalculate elapsed time and distance relative to lap start
    const adjustedGpsPoints = this.adjustTelemetryToLapStart(gpsPoints)

    // Extract other telemetry types (now required, not optional)
    const leanAngle = fullTelemetry.leanAngle
      ? this.adjustTelemetryToLapStart(
          this.extractPointsInTimeRange(
            fullTelemetry.leanAngle,
            startMs,
            finishMs
          )
        )
      : []

    const acceleration = fullTelemetry.acceleration
      ? this.adjustTelemetryToLapStart(
          this.extractPointsInTimeRange(
            fullTelemetry.acceleration,
            startMs,
            finishMs
          )
        )
      : []

    const gForce = fullTelemetry.gForce
      ? this.adjustTelemetryToLapStart(
          this.extractPointsInTimeRange(fullTelemetry.gForce, startMs, finishMs)
        )
      : []

    // Calculate statistics for this lap
    const statistics = this.calculateLapStatistics(
      adjustedGpsPoints,
      leanAngle,
      acceleration,
      gForce
    )

    return {
      gpsPoints: adjustedGpsPoints,
      leanAngle,
      acceleration,
      gForce,
      statistics,
    }
  }

  /**
   * Extract points within time range
   */
  private extractPointsInTimeRange<T extends { timestamp: Date }>(
    points: T[],
    startMs: number,
    finishMs: number
  ): T[] {
    return points.filter((point) => {
      const pointMs = point.timestamp.getTime()
      return pointMs >= startMs && pointMs <= finishMs
    })
  }

  /**
   * Adjust telemetry to lap start (reset elapsed time and distance to 0)
   */
  private adjustTelemetryToLapStart<
    T extends { elapsedMs: number; distanceMeters: number },
  >(points: T[]): T[] {
    if (points.length === 0) return points

    const startElapsedMs = points[0].elapsedMs
    const startDistance = points[0].distanceMeters

    return points.map((point) => ({
      ...point,
      elapsedMs: point.elapsedMs - startElapsedMs,
      distanceMeters: point.distanceMeters - startDistance,
    }))
  }

  /**
   * Calculate statistics for lap telemetry
   */
  private calculateLapStatistics(
    gpsPoints: GpsPoint[],
    leanAngle: LeanAnglePoint[],
    acceleration: AccelerationPoint[],
    gForce: GForcePoint[]
  ): TelemetryStatistics {
    if (gpsPoints.length === 0) {
      return {
        maxSpeedKmh: 0,
        avgSpeedKmh: 0,
        totalDistanceMeters: 0,
        totalDurationMs: 0,
        dataPointCount: 0,
        samplingFrequencyHz: 0,
        maxLeanAngle: 0,
        maxLongitudinalG: 0,
        maxLateralG: 0,
        maxCombinedG: 0,
        maxAccelerationMs2: 0,
        maxBrakingMs2: 0,
      }
    }

    const speeds = gpsPoints.map((p) => p.speedKmh)
    const maxSpeedKmh = Math.max(...speeds)
    const avgSpeedKmh = speeds.reduce((sum, s) => sum + s, 0) / speeds.length
    const totalDistanceMeters = gpsPoints[gpsPoints.length - 1].distanceMeters
    const totalDurationMs = gpsPoints[gpsPoints.length - 1].elapsedMs
    const samplingFrequencyHz = calculateSamplingFrequencyHz(gpsPoints)

    const stats: TelemetryStatistics = {
      maxSpeedKmh: Math.round(maxSpeedKmh * 100) / 100,
      avgSpeedKmh: Math.round(avgSpeedKmh * 100) / 100,
      totalDistanceMeters: Math.round(totalDistanceMeters),
      totalDurationMs,
      dataPointCount: gpsPoints.length,
      samplingFrequencyHz: Math.round(samplingFrequencyHz * 10) / 10,
      maxLeanAngle: 0,
      maxLongitudinalG: 0,
      maxLateralG: 0,
      maxCombinedG: 0,
      maxAccelerationMs2: 0,
      maxBrakingMs2: 0,
    }

    // Calculate lean angle stats
    if (leanAngle.length > 0) {
      const maxLeanAngle = Math.max(
        ...leanAngle.map((p) => Math.abs(p.leanAngleDegrees))
      )
      stats.maxLeanAngle = Math.round(maxLeanAngle * 100) / 100
    }

    // Calculate acceleration stats
    if (acceleration.length > 0) {
      const longitudinalValues = acceleration.map((p) => p.longitudinalMs2)
      stats.maxAccelerationMs2 = Math.max(
        ...longitudinalValues.filter((v) => v > 0),
        0
      )
      stats.maxBrakingMs2 = Math.abs(
        Math.min(...longitudinalValues.filter((v) => v < 0), 0)
      )
    }

    // Calculate g-force stats
    if (gForce.length > 0) {
      stats.maxLongitudinalG = Math.max(
        ...gForce.map((p) => Math.abs(p.longitudinalG))
      )
      stats.maxLateralG = Math.max(...gForce.map((p) => Math.abs(p.lateralG)))
      stats.maxCombinedG = Math.max(...gForce.map((p) => p.combinedG))
    }

    return stats
  }
}
